/**
 * narrativeScript.ts
 * Complete static narrative script for Starfall Courier.
 *
 * All content is bundled locally — zero network calls required.
 * Reference STORY.md for the full story bible and design intent.
 *
 * @module narrativeScript
 */

import type { NarrativeScript } from '../types/narrative';

/** The complete narrative script for all five worlds. */
export const NARRATIVE_SCRIPT: NarrativeScript = {
  // ─────────────────────────────────────────────────────────────
  // INTRO CUTSCENE
  // Shown on first game launch only. ~30 seconds.
  // ─────────────────────────────────────────────────────────────
  intro: {
    lines: [
      {
        speaker: 'DISPATCHER',
        text: 'Class Seven Starfall event detected. Constellation lines failing across all sectors.',
      },
      {
        speaker: 'DISPATCHER',
        text: 'Whole regions are disappearing from the star-map. We need volunteers—',
      },
      {
        speaker: 'NOVA',
        text: 'Then send me first. I\'m the fastest you\'ve got.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // HUB WORLD MAP — LORE LINES
  // Displayed when a world gate is selected on the hub map.
  // ─────────────────────────────────────────────────────────────
  worldLoreLines: {
    1: {
      worldId: 1,
      worldName: 'Sunmeadow Cliffs',
      constellation: 'Dawn Constellation',
      line: 'Sunmeadow. The Gale Rooster has stirred the winds and snapped the star-lines. Restore the feathers of the Dawn Constellation.',
    },
    2: {
      worldId: 2,
      worldName: 'Starfield Drift',
      constellation: 'Drift Constellation',
      line: 'The star-routes between worlds are shattered. Board the Courier Ship and fly the Drift — the constellation shards are scattered across open space.',
    },
    3: {
      worldId: 3,
      worldName: 'Brasscloud Foundry',
      constellation: 'Forge Constellation',
      line: 'Brasscloud Foundry. The old machines are hammering the star-lines into sparks. Restore the Forge Constellation.',
    },
    4: {
      worldId: 4,
      worldName: 'Crystal Tide Ruins',
      constellation: 'Prism Tide Constellation',
      line: 'Crystal Tide Ruins. The old waters are bending the stars into broken mirrors. Restore the Prism Tide Constellation.',
    },
    5: {
      worldId: 5,
      worldName: 'Eclipse Bastion',
      constellation: 'Crown of Night',
      line: 'Eclipse Bastion. Something waits where the sky went dark. Restore the final constellation before the Star-Eater swallows the map.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // BOSS INTRO TAUNTS
  // Displayed as subtitle text for 3 seconds before the boss fight.
  // ─────────────────────────────────────────────────────────────
  bossTaunts: {
    1: {
      worldId: 1,
      bossName: 'The Gale Rooster',
      taunt: 'Chickens don\'t belong in the sky, courier.',
    },
    2: {
      worldId: 2,
      bossName: 'Void Mothership',
      taunt: 'One small ship against the fleet. How\u2026 courier of you.',
    },
    3: {
      worldId: 3,
      bossName: 'King Rivet',
      taunt: 'Unauthorized courier detected. Preparing permanent repair.',
    },
    4: {
      worldId: 4,
      bossName: 'The Prism Leviathan',
      taunt: 'You chase reflections, little runner. Which one is really you?',
    },
    5: {
      worldId: 5,
      bossName: 'The Star-Eater',
      taunt: 'Run all you like, courier. Every road ends in my dark.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // WORLD COMPLETE LINES
  // Displayed on the world-complete screen after a boss is defeated.
  // ─────────────────────────────────────────────────────────────
  worldCompleteTexts: {
    1: {
      worldId: 1,
      text: 'With Sunmeadow\'s winds tamed, the first constellation mends — and the next fractured world calls Nova forward.',
    },
    2: {
      worldId: 2,
      text: 'The Drift is clear, the star-routes hum again, and Nova\u2019s ship banks toward the next horizon.',
    },
    3: {
      worldId: 3,
      text: 'The foundry cools, the gears slow, and the Forge Constellation locks back into place.',
    },
    4: {
      worldId: 4,
      text: 'The tide clears, the ruins sing, and the reflected stars finally point home.',
    },
    5: {
      worldId: 5,
      text: 'The last fracture closes. The sky remembers its shape.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // FINAL ENDING CUTSCENE
  // Shown after the Star-Eater is defeated.
  // ─────────────────────────────────────────────────────────────
  ending: {
    lines: [
      {
        speaker: 'NOVA',
        text: 'One piece of the sky, back where it belongs.',
      },
    ],
    closingText:
      'The routes are open. The stars are whole. And wherever the sky calls next, Nova will be the first to answer.',
  },

  // ─────────────────────────────────────────────────────────────
  // NOVA BARKS
  // Short in-game lines. Triggered by gameplay events.
  // Use sparingly — max once per level trigger type.
  // ─────────────────────────────────────────────────────────────
  novaBarkSet: {
    checkpointActivate: [
      'Locked in.',
      'Route saved.',
      'Good anchor.',
      'Marked. Moving on.',
      'That\'ll hold.',
    ],

    shardCollected: [
      'There\'s one.',
      'Sky piece, found.',
      'One more for the map.',
      'The stars remember this shape.',
      'Almost feels warm.',
    ],

    seedMilestone: [
      'Seeds adding up.',
      'Keep them coming.',
      'Pockets getting heavy.',
      'Every seed counts.',
    ],

    secretRoomFound: [
      '...Wait. This wasn\'t on the map.',
      'Someone hid this.',
      'Off-route and worth it.',
      'Good thing I looked twice.',
    ],

    bossDefeated: {
      1: 'Patch the sky, chase a rogue rooster. Easy.',
      2: 'One fleet down. And the lift in my boots? That\'s new.',
      3: 'Next time, stay bolted down.',
      4: 'Stop chasing mirrors.',
      5: 'The dark\'s got an end after all.',
    },

    lowHealth: [
      'Okay. Okay. Focus.',
      'Not today.',
      'Still standing.',
      'One more hit and...',
    ],
  },
};
